import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { useDebounced } from "@/lib/useDebounced";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Customers() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ display_name: "", type: "individual", company_name: "", vat_number: "" });

  useEffect(() => { setPage(1); }, [debouncedQ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get("/customers", { params: { page, limit: 20, q: debouncedQ || undefined } })
       .then((res) => { if (!cancelled) setData(res.data); })
       .catch((err) => { if (!cancelled) setError(err); })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debouncedQ, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const create = async () => {
    try {
      const { data } = await api.post("/customers", { ...form });
      toast.success(`Customer ${data.customer_code} created`);
      setOpen(false); setForm({ display_name: "", type: "individual", company_name: "", vat_number: "" });
      setPage(1);
      retry();
    } catch (e) { toast.error(formatError(e, "Failed to create customer")); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-4">
        <div>
          <div className="label-overline">Management</div>
          <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">Customers</h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="cust-q" placeholder="Search name or code…"
                 className="sm:w-64 rounded h-10 font-mono"
                 value={q} onChange={(e) => setQ(e.target.value)} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="cust-new-btn" className="rounded bg-blue-700 hover:bg-blue-800 whitespace-nowrap">
                + New customer
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded">
              <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-overline">Type</Label>
                  <select className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono"
                          value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                          data-testid="cust-type">
                    <option value="individual">individual</option>
                    <option value="company">company</option>
                  </select>
                </div>
                <div>
                  <Label className="label-overline">Display name</Label>
                  <Input required data-testid="cust-name" className="rounded h-10 mt-1"
                         value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
                </div>
                {form.type === "company" && (
                  <>
                    <div>
                      <Label className="label-overline">Company name</Label>
                      <Input data-testid="cust-company" className="rounded h-10 mt-1"
                             value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="label-overline">VAT number (optional)</Label>
                      <Input data-testid="cust-vat" className="rounded h-10 mt-1 font-mono"
                             value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" className="rounded" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="cust-create" className="rounded bg-blue-700 hover:bg-blue-800"
                        onClick={create} disabled={!form.display_name}>Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        {error ? (
          <div className="p-4">
            <WidgetError
              error={error}
              onRetry={retry}
              title="Could not load customers"
              testId="customers-error"
            />
          </div>
        ) : (
        <table className="tbl w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">VAT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No customers match</td></tr>
            )}
            {data.items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100" data-testid={`cust-row-${c.customer_code}`}>
                <td className="px-4 py-3 font-mono">{c.customer_code}</td>
                <td className="px-4 py-3 font-semibold">{c.display_name}</td>
                <td className="px-4 py-3">{c.type}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.vat_number || "—"}</td>
                <td className="px-4 py-3">
                  <Link data-testid={`cust-open-${c.customer_code}`}
                        to={`/app/customers/${c.id}`} className="text-blue-700 font-semibold hover:underline">
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
        <PaginationBar page={data.page || 1} pages={data.pages || 1} onChange={setPage} />
      </div>
    </div>
  );
}
